<template>
  <section class="nylon-SubmissionWizard" :class="{ complete: step === steps.COMPLETE, blocked: isBlocked }">
    <header
      id="nylon-SubmissionWizard-header"
      class="nylon-SubmissionWizard-header"
      data-header-hero
      :class="{
        'step-1-active': step === steps.SETUP,
        'step-15-active': step === steps.ARTWORK,
        'step-2-active': step === steps.BUILDER,
        'step-3-active': step === steps.DETAILS,
      }"
    >
      <div class="wrapper">
        <h1>{{ headerText }}</h1>
        <nav class="nylon-SubmissionWizard-steps">
          <a href="#" class="back-link" @click.prevent="onChangeStep(steps.SETUP)" v-if="step === steps.ARTWORK || step === steps.BUILDER">Back</a>
          <a href="#" class="back-link" @click.prevent="onChangeStep(steps.BUILDER)" v-if="step === steps.DETAILS">Back</a>
          <NylonStepMarker v-for="n in 3" :key="n" :active="stepIndex(step) === n" :clickable="canJumpTo(n)" @click="onMarkerClick(n)">{{ n }}</NylonStepMarker>
        </nav>
        <i class="feedback-icon alert" v-if="isBlocked"></i>
        <i
          class="feedback-icon"
          :class="{
            success: !accountIncomplete,
            alert: accountIncomplete,
          }"
          v-else-if="step === steps.COMPLETE"
        ></i>
      </div>
    </header>

    <section class="nylon-SubmissionWizard-content" v-if="user && !user.canSubmitDesigns">
      <div class="wrapper">
        <div class="text">
          <p>This account can't submit designs. Sign out and sign in with your seller account to create a product.</p>
        </div>
        <div class="action">
          <a class="submit-another" href="/sign-out">Sign Out</a>
        </div>
      </div>
    </section>

    <section class="nylon-SubmissionWizard-content" v-else-if="user && !user.canCreateProduct">
      <div class="wrapper">
        <div class="text">
          <p>
            Slow down there, partner. Your account is limited to
            <strong>{{ user.tier.productLimit }}</strong>
            total products. You are currently using
            <strong>{{ user.numProducts }}</strong>
            /
            <strong>{{ user.tier.productLimit }}</strong>
            products. Try
            <InertiaLink href="/account/products">deleting some older products</InertiaLink>
            if you want to add new ones.
          </p>
        </div>
        <div class="action">
          <a class="submit-another" href="/account/products" target="_blank">Review Products</a>
        </div>
      </div>
    </section>

    <template v-else>
      <!-- Step 1 (product setup) -->
      <section class="nylon-SubmissionWizard-content step-1 artwork-uploaded" v-if="step === steps.SETUP">
        <div class="wrapper">
          <aside class="nylon-ProductPreviewPanel">
            <div class="content">
              <div class="nylon-Intro-text">
                <div v-if="isCustomFlow" class="sub-content" id="sub-content">
                  <article>
                    <h4><span>Are you ready?</span></h4>
                    <p>Step 1 is simple: grab your artwork file and put it in the box. Honestly, you could stop reading right now.</p>
                    <p>
                      Still here? Whether you need t-shirts, hats, mugs, totes, phone cases, or more, Cotton Bureau Custom fits the bill. After uploading your artwork, just choose a product type and
                      enter a few relevant details like a title, description, and how much money you want to make. Boom, you’re live. You can order what you need to hand out in person, or send people
                      directly to your product page to purchase on their own.
                    </p>
                    <p>
                      Do you prefer the unmatched quality of screen printed tees? Classic pre-order campaigns are available on Cotton Bureau Custom too. We’ll take a quick look to make sure we have
                      what we need for production, then you’re good to go.
                    </p>
                    <p>Good luck! We’re here if you need us.</p>
                    <p>&nbsp;</p>
                    <h4><span>What’s the catch?</span></h4>
                    <p>We’re not going to lie. Cotton Bureau Custom <em>is</em> different than Cotton Bureau in one small but important way:</p>
                    <p><strong>Cotton Bureau Custom is reserved exclusively for the non-designer, a.k.a. the rest of us.</strong> That means our typical design quality requirements will not apply.</p>
                    <p>
                      (All the normal <InertiaLink href="/terms-of-service">terms of service</InertiaLink> do, though. We reserve the right to reject or remove anyone, at any time, for any thing.)
                    </p>
                    <p>
                      To keep the streams from crossing, Custom products are by necessity <em>unlisted</em>. That means they won’t show up in search results or in any other gallery view. We think
                      that’s a pretty reasonable compromise. You get access to our entire suite of sales tools; we get to keep the core retail shopping experience pure.
                    </p>
                    <p>Sound good? Yeah, we thought so.</p>
                    <a href="#reference-pane-1" class="learn-more" v-scroll-to="'#reference-pane-1'">Still have questions? Keep Reading</a>
                  </article>
                </div>

                <div v-else class="sub-content" id="sub-content">
                  <article>
                    <h2>Getting Started</h2>
                    <p>
                      You’re a designer, web brand, agency, conference, start-up, app developer, or community. You’d love to make t-shirts and hoodies, right? Maybe a tote bag or an embroidered hat?
                      Of course you would. So why haven’t you? We’ll tell you why… it’s hard. Finding a good print shop is hard. Learning about printing is hard. Customer service is hard. Shipping is
                      hard. It’s all a big pain in the butt.
                    </p>
                    <p>
                      What if you could post your designs for sale in a high-quality, design-focused online shop and have someone else take care of all the dirty work for you? No buying hundreds of
                      shirts and hoodies upfront. No inventory. No shipping. No customer service. No risk. Sounds great, right?
                    </p>
                    <p><strong>That’s where we come in.</strong></p>
                    <p>Design and launch your own shirts, hats, drinkware, totes, phone cases, and more! Available on demand all the time, or as a simple pre-order campaign — your choice.</p>
                    <p>
                      Fair warning: we reject up to 90% of submissions. We’re not kidding when we say designs need to have it going on. The standards are high to be included in the official Cotton
                      Bureau shop.
                    </p>
                    <p>
                      <em
                        >If you know you’re not quite up to snuff, but you still need someone to help print and ship shirts for your school chess club, organic meal delivery startup, family choir, or
                        cross-country road trip, check out <InertiaLink href="/custom">Cotton Bureau Custom</InertiaLink>. It’s the same quality and service you love minus the smug designer
                        spectacles.</em
                      >
                    </p>
                    <InertiaLink href="/how-it-works" class="learn-more learn-more--create-a-product">Not ready yet? Learn more about how it all works.</InertiaLink>
                  </article>
                </div>
              </div>
            </div>
          </aside>
          <main class="nylon-SubmissionWizard-options">
            <div class="options-container">
              <header class="options-header">
                <h2>Product</h2>
                <button
                  type="button"
                  class="options-header-toggle"
                  :class="{ 'is-collapsed': collapsedBoxes.product }"
                  :aria-expanded="(!collapsedBoxes.product).toString()"
                  aria-label="Toggle section"
                  @click.prevent="onToggleBox('product')"
                ></button>
              </header>
              <NylonCollapse :collapsed="collapsedBoxes.product">
                <main class="options-content">
                  <NylonOptionPanel collapsible>
                    <template #header>
                      <h4>Product Type</h4>
                      <a href="#" class="help-link" @click.prevent="showModal(productTypeModalId)">?</a>
                    </template>
                    <NylonOptionGrid preview>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.productType === productTypes.APPAREL }" @click.prevent="onProductTypeChange(productTypes.APPAREL)">
                        <figure>
                          <NylonIcon icon-name="tee-detail-standard" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Shirt</span>
                        </div>
                      </button>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.productType === productTypes.DRINKWARE }" @click.prevent="onProductTypeChange(productTypes.DRINKWARE)">
                        <figure>
                          <NylonIcon icon-name="pint-glass-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Drinkware</span>
                        </div>
                      </button>
                      <button
                        class="nylon-OptionGrid-option"
                        :class="{ active: input.productType === productTypes.HAT }"
                        @click.prevent="onProductTypeChange(productTypes.HAT)"
                        v-if="user && user.tier.tier >= 2"
                      >
                        <figure>
                          <NylonIcon icon-name="curved-cap-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Hat</span>
                        </div>
                      </button>
                      <button
                        class="nylon-OptionGrid-option"
                        :class="{ active: input.productType === productTypes.BEANIE }"
                        @click.prevent="onProductTypeChange(productTypes.BEANIE)"
                        v-if="user && user.tier.tier >= 2"
                      >
                        <figure>
                          <NylonIcon icon-name="beanie-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Beanie</span>
                        </div>
                      </button>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.productType === productTypes.TOTE }" @click.prevent="onProductTypeChange(productTypes.TOTE)">
                        <figure>
                          <NylonIcon icon-name="tote-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Tote</span>
                        </div>
                      </button>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.productType === productTypes.PHONECASE }" @click.prevent="onProductTypeChange(productTypes.PHONECASE)">
                        <figure>
                          <NylonIcon icon-name="phone-case-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Phone Case</span>
                        </div>
                      </button>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.productType === productTypes.STICKER }" @click.prevent="onProductTypeChange(productTypes.STICKER)">
                        <figure>
                          <NylonIcon icon-name="sticker-detail" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">Sticker</span>
                        </div>
                      </button>
                    </NylonOptionGrid>
                  </NylonOptionPanel>

                  <NylonOptionPanel collapsible v-if="input.productType === productTypes.APPAREL">
                    <template #header>
                      <h4>Sales Method</h4>
                      <a href="#" class="help-link" @click.prevent="showModal(salesMethodModalId)">?</a>
                    </template>
                    <NylonOptionGrid>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.salesMethod === salesMethods.ONDEMAND }" @click.prevent="onSalesMethodChange(salesMethods.ONDEMAND)">
                        <figure>
                          <NylonIcon icon-name="rocket" class="icon" />
                        </figure>
                        <div class="text">
                          <span class="heading">On-Demand</span>
                          <p>Available 24/7. Ships in a few days.</p>
                        </div>
                      </button>
                      <button class="nylon-OptionGrid-option" :class="{ active: input.salesMethod === salesMethods.CAMPAIGN }" @click.prevent="onSalesMethodChange(salesMethods.CAMPAIGN)">
                        <figure>
                          <NylonIcon icon-name="hourglass" class="icon icon--hourglass" />
                        </figure>
                        <div class="text">
                          <span class="heading">Campaign</span>
                          <p>Available for a short time. Ships in a few weeks.</p>
                        </div>
                      </button>
                    </NylonOptionGrid>
                  </NylonOptionPanel>
                </main>
              </NylonCollapse>
            </div>
            <footer class="options-footer">
              <nav class="options-footer-actions">
                <button class="next" @click.prevent="onCompleteSetup" :disabled="!canLeaveSetup">Next</button>
              </nav>
            </footer>
          </main>
        </div>
      </section>

      <!-- Step 2 for APPAREL (entry state + builder) + step 3 details -->
      <section class="nylon-SubmissionWizard-content step-2" v-else-if="step === steps.ARTWORK || step === steps.BUILDER || step === steps.DETAILS">
        <div class="wrapper">
          <!-- Artwork entry state: no artwork exists yet. Every product type
               renders a live mockup of its default item wearing the dashed
               print-area placeholder (the same placeholder artwork the back
               preview uses pre-upload) — drinkware as the 3D model the
               post-upload preview uses — so the entry state matches the real
               preview. A failed render leaves the panel empty (no spinner, no
               broken image): the preview is decorative until artwork exists. -->
          <aside class="nylon-ProductPreviewPanel" v-if="step === steps.ARTWORK">
            <header>
              <h4>
                Preview
                <span class="u-hideAboveMobile">Product</span>
              </h4>
            </header>
            <div class="product-preview">
              <ThreeDimensionalModel
                v-if="input.productType === productTypes.DRINKWARE"
                :src="defaultDrinkware.model"
                :artwork="entryPlaceholderTextureUrl"
                :camera="defaultDrinkware.camera"
              ></ThreeDimensionalModel>
              <template v-else>
                <img
                  v-show="entryPlaceholderLoaded && !entryPlaceholderFailed"
                  class="nylon-FileDisplay-image"
                  :src="entryPlaceholderMockupUrl"
                  @load="entryPlaceholderLoaded = true"
                  @error="entryPlaceholderFailed = true"
                  alt="Blank product with the print area marked"
                />
                <figure v-show="!entryPlaceholderLoaded && !entryPlaceholderFailed" class="product-image-placeholder">
                  <div><ImagePending /></div>
                </figure>
              </template>
            </div>
          </aside>
          <transition name="fade">
            <NylonProductPreviewPanel
              v-if="step !== steps.ARTWORK"
              :product-type="input.productType"
              :show-raster="showRaster"
              :show-mobile-preview="showMobilePreview"
              :active-item="activeItem"
              :active-color="activeColor"
              :colors="input.colors"
              :filtered-bundle-items="filteredBundleItems"
              :artwork-path-info="input.artwork.rasterArtworkPathInfo"
              :placement="input.placement"
              :print-geometry="input.printGeometry"
              :print-offset="input.printOffset"
              :second-location="input.secondLocation"
              :mockup-width="mockupWidth"
              :mockup-query="mockupQuery"
              @select-color-preview="onSelectColorPreview"
              @select-item-preview="onSelectItemPreview"
              @update:show-mobile-preview="showMobilePreview = $event"
            />
          </transition>
          <main class="nylon-SubmissionWizard-options" v-if="step === steps.ARTWORK || step === steps.BUILDER">
            <div class="options-container" id="artwork-container">
              <header class="options-header">
                <!-- The front/back qualifier appears only once a second location
                     exists; other product types have no second location, so they
                     always read plain "Artwork". -->
                <h2>{{ input.secondLocation ? "Artwork — Front" : "Artwork" }}</h2>
                <button class="show-preview" @click.prevent="showMobilePreview = !showMobilePreview">Preview</button>
                <button
                  type="button"
                  class="options-header-toggle"
                  :class="{ 'is-collapsed': collapsedBoxes.artwork }"
                  :aria-expanded="(!collapsedBoxes.artwork).toString()"
                  aria-label="Toggle section"
                  @click.prevent="onToggleBox('artwork')"
                ></button>
              </header>
              <NylonCollapse :collapsed="collapsedBoxes.artwork">
                <main class="options-content">
                  <NylonOptionPanel collapsible compact-footer>
                    <template #header>
                      <h4>Design File</h4>
                      <i class="feedback-icon success" v-if="input.artwork.rasterArtworkPathInfo"></i>
                    </template>
                    <div :class="{ 'upload-panel-embed': !input.artwork.rasterArtworkPathInfo }">
                      <vue-dropzone
                        class="change-artwork nylon-Dropzone"
                        :class="{ 'has-artwork': !!input.artwork.rasterArtworkPathInfo }"
                        ref="rasterArtworkDropzone2"
                        id="raster-artwork-dropzone2"
                        :options="dropzoneRasterOptions"
                        :useCustomSlot="true"
                        :include-styling="true"
                        @vdropzone-file-added="dzRasterArtworkAdded2"
                        @vdropzone-success="dzRasterArtworkSuccess2"
                        @vdropzone-error="dzRasterArtworkError2"
                        @vdropzone-max-files-exceeded="dzRasterArtworkMaxFilesExceeded2"
                      >
                        <div class="nylon-FileDisplay">
                          <div
                            class="nylon-FileDisplay-wrapper"
                            v-if="showRaster"
                            v-show="
                              imageLoaded(
                                artworkQuery({
                                  pathInfo: input.artwork.rasterArtworkPathInfo,
                                  w: 1000,
                                  local: true,
                                }),
                              )
                            "
                          >
                            <img
                              class="nylon-FileDisplay-image"
                              :src="
                                imageUrl(
                                  artworkQuery({
                                    pathInfo: input.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              @load="
                                onImageLoaded(
                                  artworkQuery({
                                    pathInfo: input.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              @error="
                                onImageError(
                                  artworkQuery({
                                    pathInfo: input.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              alt="Artwork"
                            />
                          </div>
                          <figure
                            v-show="
                              showRaster &&
                              imageLoading(
                                artworkQuery({
                                  pathInfo: input.artwork.rasterArtworkPathInfo,
                                  w: 1000,
                                  local: true,
                                }),
                              )
                            "
                            class="product-image-placeholder"
                          >
                            <div><ImagePending /></div>
                          </figure>
                        </div>
                        <div class="content">
                          <template v-if="input.artwork.rasterArtworkPathInfo">
                            <button class="browse">
                              <span>Replace Artwork</span>
                            </button>
                          </template>
                          <template v-else>
                            <figure></figure>
                            <h3>Drag PNG File Here</h3>
                            <span class="or">or</span>
                            <button class="browse">
                              <span class="u-hideAboveMobile">Upload File</span>
                              <span class="u-hideOnMobile">Browse</span>
                            </button>
                            <p class="u-hideAboveMobile">
                              Files
                              <em>must</em>
                              be PNG-24 format, at least 4000 pixels on one side, with a transparent background.
                            </p>
                            <p class="u-hideOnMobile">
                              Files
                              <em>must</em>
                              be PNG-24 format, at least 4000 pixels on one side, with a transparent background.
                            </p>
                          </template>
                        </div>
                      </vue-dropzone>
                    </div>
                    <template #footer v-if="input.productionMethod !== productionMethods.EMBROIDERY">
                      <div class="options-wrapper">
                        <ul class="options">
                          <li class="option">
                            <input type="checkbox" id="noTrim" @click="toggleNoTrim" v-model="noTrim" />
                            <label for="noTrim">Preserve artwork padding</label>
                          </li>
                        </ul>
                      </div>
                    </template>
                  </NylonOptionPanel>

                  <!-- Everything below Design File stays hidden on the entry state until
                     artwork is on file. Step-scoped so a replace-in-flight on the builder
                     (which nulls the pathInfo while uploading) doesn't collapse them. -->
                  <template v-if="step !== steps.ARTWORK || input.artwork.rasterArtworkPathInfo">
                    <NylonOptionPanel collapsible v-if="input.productType === productTypes.APPAREL">
                      <template #header>
                        <h4>Decoration Method</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(decorationMethodModalId)">?</a>
                      </template>
                      <NylonOptionGrid>
                        <button
                          class="nylon-OptionGrid-option"
                          v-if="input.salesMethod === salesMethods.ONDEMAND"
                          :class="{ active: input.productionMethod === productionMethods.DIRECTTOGARMENT }"
                          @click.prevent="onProductionMethodChange(productionMethods.DIRECTTOGARMENT)"
                        >
                          <figure>
                            <NylonIcon icon-name="dtg" class="icon" />
                          </figure>
                          <div class="text">
                            <span class="heading">Direct To Garment</span>
                            <p>Printed with teeny tiny dots of cyan, magenta, yellow, and black.</p>
                          </div>
                        </button>
                        <button
                          class="nylon-OptionGrid-option"
                          v-if="input.salesMethod === salesMethods.CAMPAIGN"
                          :class="{ active: input.productionMethod === productionMethods.SCREENPRINT }"
                          @click.prevent="onProductionMethodChange(productionMethods.SCREENPRINT)"
                        >
                          <figure>
                            <NylonIcon icon-name="screen" class="icon" view-box="0 0 214.52 260.11" />
                          </figure>
                          <div class="text">
                            <span class="heading">Screen Printing</span>
                            <p>Traditional, hand-pulled ink-on-fabric.</p>
                          </div>
                        </button>
                        <button
                          class="nylon-OptionGrid-option"
                          v-if="input.salesMethod === salesMethods.ONDEMAND || input.salesMethod === salesMethods.CAMPAIGN"
                          :class="{ active: input.productionMethod === productionMethods.EMBROIDERY }"
                          @click.prevent="onProductionMethodChange(productionMethods.EMBROIDERY)"
                        >
                          <figure>
                            <NylonIcon icon-name="thread" class="icon" />
                          </figure>
                          <div class="text">
                            <span class="heading">Embroidery</span>
                            <p>So, a needle pulling thread.</p>
                          </div>
                        </button>
                      </NylonOptionGrid>
                    </NylonOptionPanel>

                    <NylonOptionPanel collapsible v-if="input.productionMethod === productionMethods.SCREENPRINT">
                      <template #header>
                        <h4>Ink Colors</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(inkNumOptionsModalId)">?</a>
                      </template>
                      <NylonOptionGrid compact>
                        <button v-for="inkCount in [1, 2, 3, 4, 5, 6, 7, 8]" class="nylon-OptionGrid-option" :class="{ active: inkCount == input.numInks }" @click.prevent="onClickNumInks(inkCount)">
                          <div class="text">
                            <span class="heading">{{ inkCount }}</span>
                          </div>
                        </button>
                      </NylonOptionGrid>
                    </NylonOptionPanel>

                    <NylonOptionPanel collapsible v-if="input.productionMethod === productionMethods.SCREENPRINT">
                      <template #header>
                        <h4>Ink Type</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(inkTypeOptionsModalId)">?</a>
                      </template>
                      <NylonOptionGrid>
                        <button
                          v-for="inkType in inkTypes"
                          class="nylon-OptionGrid-option"
                          :class="{ active: input.inkType === inkType.name }"
                          @click.prevent="onInkTypeChange(inkType.name)"
                          :title="inkType.title"
                        >
                          <div class="text">
                            <span class="heading">{{ inkType.heading }}</span>
                            <p>{{ inkType.description }}</p>
                          </div>
                        </button>
                      </NylonOptionGrid>
                    </NylonOptionPanel>

                    <NylonOptionPanel collapsible v-if="input.productionMethod === productionMethods.SCREENPRINT">
                      <template #header>
                        <h4>Ink Options</h4>
                      </template>
                      <NylonOptionGrid>
                        <button
                          v-for="inkEffect in inkEffects"
                          class="nylon-OptionGrid-option"
                          :class="{ active: input.inkEffect === inkEffect.name }"
                          @click.prevent="onInkEffectChange(inkEffect.name)"
                          :title="inkEffect.title"
                        >
                          <div class="text">
                            <span class="heading">{{ inkEffect.heading }}</span>
                            <p>{{ inkEffect.description }}</p>
                          </div>
                        </button>
                      </NylonOptionGrid>
                    </NylonOptionPanel>

                    <NylonOptionPanel collapsible v-if="[productionMethods.SCREENPRINT].includes(input.productionMethod)">
                      <template #header>
                        <h4>Source File</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(sourceFileModalId)">?</a>
                      </template>
                      <p>Screen printed pre-order campaigns require color-separated files in either vector or hi-resolution raster format.</p>
                      <div class="drop-zone">
                        <vue-dropzone
                          class="nylon-Dropzone"
                          ref="sourceArtworkDropzone"
                          id="source-artwork-dropzone"
                          :options="dropzoneSourceOptions"
                          :useCustomSlot="true"
                          :include-styling="true"
                          @vdropzone-file-added="dzSourceArtworkAdded"
                          @vdropzone-success="dzSourceArtworkSuccess"
                          @vdropzone-error="dzSourceArtworkError"
                          @vdropzone-max-files-exceeded="dzSourceArtworkMaxFilesExceeded"
                        >
                          <div class="content">
                            <template v-if="input.artwork.sourceArtworkPathInfo">
                              <h3>Source Artwork is already on file</h3>
                            </template>
                            <template v-else>
                              <h3>Drag File Here</h3>
                              <span class="or">or</span>
                              <button class="browse">Browse</button>
                              <p>Accepted file formats are: PSD, AI, SVG, PDF, EPS</p>
                            </template>
                          </div>
                        </vue-dropzone>
                      </div>
                    </NylonOptionPanel>

                    <div class="disclaimer disclaimer--caution active" v-if="input.productionMethod === productionMethods.EMBROIDERY">
                      <figure>
                        <span>!</span>
                      </figure>
                      <div class="figcaption">
                        <p>
                          Thread colors look funny? Not sure what size to make the artwork?
                          <a href="https://support.cottonbureau.com/article/201-what-do-i-need-to-know-before-i-submit-my-first-hat">Read this first</a>.
                        </p>
                        <p><strong>Note: We strongly recommend sticking with medium (or smaller) placement size for most designs.</strong></p>
                      </div>
                    </div>

                    <NylonOptionPanel
                      collapsible
                      v-if="[productTypes.APPAREL, productTypes.PHONECASE, productTypes.HAT, productTypes.BEANIE, productTypes.TOTE, productTypes.DRINKWARE].includes(input.productType)"
                    >
                      <template #header>
                        <h4 id="placement-heading">Design Placement</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(placementModalId)">?</a>
                      </template>
                      <!-- Front or back: a product may print its one location on the back (PRO-3173). -->
                      <template #headerControl v-if="canChooseBackPrimary">
                        <NylonSegmentedControl :model-value="primarySide" :options="primarySideOptions" aria-labelledby="placement-heading" @update:model-value="onPrimarySideChange" />
                      </template>
                      <NylonPlacementOptions
                        :placements="placements"
                        :placement="input.placement"
                        :debounced-input="debouncedInput"
                        :custom-size="customSize"
                        :product-type="input.productType"
                        :production-method="input.productionMethod"
                        :disable-offset-y="primaryIsBackNeck"
                        :offset-range="offsetRange"
                        @placement-change="onPlacementChange"
                        @print-width-input="onPrintWidthInput($event, debouncedInput.printGeometry, input.printGeometry)"
                        @print-height-input="onPrintHeightInput($event, debouncedInput.printGeometry, input.printGeometry)"
                        @offset-x-input="onOffsetXInput"
                        @offset-y-input="onOffsetYInput"
                      />
                      <template #footer v-if="[productTypes.PHONECASE, productTypes.DRINKWARE].includes(input.productType) && input.placement === 'max+centered'">
                        <div class="options-wrapper">
                          <ul class="options">
                            <li class="option">
                              <input type="checkbox" id="noScale" v-model="input.mockupOptions.noScale" />
                              <label for="noScale">Do not scale artwork with case size</label>
                            </li>
                          </ul>
                        </div>
                      </template>
                    </NylonOptionPanel>
                    <!-- Mirrors the server gate (Product::supportsBackPrint): on-demand DTG or
                         screen-printed campaign apparel, with a front/pocket primary. -->
                    <NylonAddButton v-if="step === steps.BUILDER && canAddSecondLocation" @click="onAddSecondLocation">Add a Back Print</NylonAddButton>
                  </template>
                </main>
              </NylonCollapse>
            </div>
            <div class="options-container" id="second-location-container" v-if="input.secondLocation">
              <header class="options-header">
                <h2>Artwork — Back</h2>
                <button type="button" class="options-header-remove" aria-label="Remove back print" title="Remove" @click.prevent="onRemoveSecondLocation"></button>
                <button
                  type="button"
                  class="options-header-toggle"
                  :class="{ 'is-collapsed': collapsedBoxes.secondLocation }"
                  :aria-expanded="(!collapsedBoxes.secondLocation).toString()"
                  aria-label="Toggle section"
                  @click.prevent="onToggleBox('secondLocation')"
                ></button>
              </header>
              <NylonCollapse :collapsed="collapsedBoxes.secondLocation">
                <main class="options-content">
                  <NylonOptionPanel collapsible compact-footer>
                    <template #header>
                      <h4>Design File</h4>
                      <i class="feedback-icon success" v-if="input.secondLocation?.artwork.rasterArtworkPathInfo"></i>
                    </template>
                    <div :class="{ 'upload-panel-embed': !input.secondLocation?.artwork.rasterArtworkPathInfo }">
                      <vue-dropzone
                        class="change-artwork nylon-Dropzone"
                        :class="{ 'has-artwork': !!input.secondLocation?.artwork.rasterArtworkPathInfo }"
                        ref="secondRasterArtworkDropzone"
                        id="second-raster-artwork-dropzone"
                        :options="dropzoneRasterOptions"
                        :useCustomSlot="true"
                        :include-styling="true"
                        @vdropzone-file-added="dzSecondRasterArtworkAdded"
                        @vdropzone-success="dzSecondRasterArtworkSuccess"
                        @vdropzone-error="dzSecondRasterArtworkError"
                        @vdropzone-max-files-exceeded="dzSecondRasterArtworkMaxFilesExceeded"
                      >
                        <div class="nylon-FileDisplay">
                          <div
                            class="nylon-FileDisplay-wrapper"
                            v-if="showSecondRaster"
                            v-show="
                              imageLoaded(
                                artworkQuery({
                                  pathInfo: input.secondLocation?.artwork.rasterArtworkPathInfo,
                                  w: 1000,
                                  local: true,
                                }),
                              )
                            "
                          >
                            <img
                              class="nylon-FileDisplay-image"
                              :src="
                                imageUrl(
                                  artworkQuery({
                                    pathInfo: input.secondLocation?.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              @load="
                                onImageLoaded(
                                  artworkQuery({
                                    pathInfo: input.secondLocation?.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              @error="
                                onImageError(
                                  artworkQuery({
                                    pathInfo: input.secondLocation?.artwork.rasterArtworkPathInfo,
                                    w: 1000,
                                    local: true,
                                  }),
                                )
                              "
                              alt="Back print artwork"
                            />
                          </div>
                          <figure
                            v-show="
                              showSecondRaster &&
                              imageLoading(
                                artworkQuery({
                                  pathInfo: input.secondLocation?.artwork.rasterArtworkPathInfo,
                                  w: 1000,
                                  local: true,
                                }),
                              )
                            "
                            class="product-image-placeholder"
                          >
                            <div><ImagePending /></div>
                          </figure>
                        </div>
                        <div class="content">
                          <template v-if="input.secondLocation?.artwork.rasterArtworkPathInfo">
                            <button class="browse">
                              <span>Replace Artwork</span>
                            </button>
                          </template>
                          <template v-else>
                            <figure></figure>
                            <h3>Drag PNG File Here</h3>
                            <span class="or">or</span>
                            <button class="browse">
                              <span class="u-hideAboveMobile">Upload File</span>
                              <span class="u-hideOnMobile">Browse</span>
                            </button>
                            <p class="u-hideAboveMobile">
                              Files
                              <em>must</em>
                              be PNG-24 format, at least 4000 pixels on one side, with a transparent background.
                            </p>
                            <p class="u-hideOnMobile">
                              Files
                              <em>must</em>
                              be PNG-24 format, at least 4000 pixels on one side, with a transparent background.
                            </p>
                          </template>
                        </div>
                      </vue-dropzone>
                    </div>
                    <template #footer>
                      <div class="options-wrapper">
                        <ul class="options">
                          <li class="option">
                            <input type="checkbox" id="secondNoTrim" @click="toggleSecondNoTrim" v-model="secondNoTrim" />
                            <label for="secondNoTrim">Preserve artwork padding</label>
                          </li>
                        </ul>
                      </div>
                    </template>
                  </NylonOptionPanel>

                  <!-- The back print is printed the primary's way (enforced by the
                       add-location gate), so there is no second Decoration Method choice;
                       a screen-printed one (PRO-3173) adds its own inks and source file.
                       Progressive disclosure mirrors the front: the rest appears once
                       the back upload is underway. -->
                  <NylonOptionPanel collapsible v-if="showSecondRaster || isSecondRasterFileUploading">
                    <template #header>
                      <h4>Design Placement</h4>
                      <a href="#" class="help-link" @click.prevent="showModal(placementModalId)">?</a>
                    </template>
                    <NylonPlacementOptions
                      :placements="secondPlacements"
                      :placement="input.secondLocation.placement"
                      :debounced-input="debouncedSecondInput"
                      :custom-size="secondCustomSize"
                      :product-type="input.productType"
                      :production-method="input.secondLocation.productionMethod"
                      :disable-offset-y="secondIsBackNeck"
                      :offset-range="secondOffsetRange"
                      @placement-change="onSecondPlacementChange"
                      @print-width-input="onSecondPrintWidthInput"
                      @print-height-input="onSecondPrintHeightInput"
                      @offset-x-input="onSecondOffsetXInput"
                      @offset-y-input="onSecondOffsetYInput"
                    />
                  </NylonOptionPanel>

                  <template v-if="secondIsScreenPrint && (showSecondRaster || isSecondRasterFileUploading)">
                    <ScreenPrintInkPanels
                      v-model:num-inks="input.secondLocation.numInks"
                      v-model:ink-type="input.secondLocation.inkType"
                      v-model:ink-effect="input.secondLocation.inkEffect"
                      :ink-types="inkTypes"
                      :ink-effects="inkEffects"
                      @help="showModal($event === 'numInks' ? inkNumOptionsModalId : inkTypeOptionsModalId)"
                    />

                    <NylonOptionPanel collapsible>
                      <template #header>
                        <h4>Source File</h4>
                        <a href="#" class="help-link" @click.prevent="showModal(sourceFileModalId)">?</a>
                      </template>
                      <p>Screen printed pre-order campaigns require color-separated files in either vector or hi-resolution raster format.</p>
                      <div class="drop-zone">
                        <vue-dropzone
                          class="nylon-Dropzone"
                          ref="secondSourceArtworkDropzone"
                          id="second-source-artwork-dropzone"
                          :options="dropzoneSourceOptions"
                          :useCustomSlot="true"
                          :include-styling="true"
                          @vdropzone-file-added="dzSecondSourceArtworkAdded"
                          @vdropzone-success="dzSecondSourceArtworkSuccess"
                          @vdropzone-error="dzSecondSourceArtworkError"
                          @vdropzone-max-files-exceeded="dzSecondSourceArtworkMaxFilesExceeded"
                        >
                          <div class="content">
                            <template v-if="input.secondLocation.artwork.sourceArtworkPathInfo">
                              <h3>Source Artwork is already on file</h3>
                            </template>
                            <template v-else>
                              <h3>Drag File Here</h3>
                              <span class="or">or</span>
                              <button class="browse">Browse</button>
                              <p>Accepted file formats are: PSD, AI, SVG, PDF, EPS</p>
                            </template>
                          </div>
                        </vue-dropzone>
                      </div>
                    </NylonOptionPanel>
                  </template>
                </main>
              </NylonCollapse>
            </div>
            <div class="options-container" v-if="step === steps.BUILDER">
              <header class="options-header">
                <!-- "Shirt Options" is apparel vocabulary; other product types keep
                     the generic "Colors". -->
                <h2>{{ input.productType === productTypes.APPAREL ? "Shirt Options" : "Colors" }}</h2>
                <button
                  type="button"
                  class="options-header-toggle"
                  :class="{ 'is-collapsed': collapsedBoxes.colors }"
                  :aria-expanded="(!collapsedBoxes.colors).toString()"
                  aria-label="Toggle section"
                  @click.prevent="onToggleBox('colors')"
                ></button>
              </header>
              <NylonCollapse :collapsed="collapsedBoxes.colors">
                <main class="options-content">
                  <NylonColorStylePanel
                    collapsible
                    :product-type="input.productType"
                    :colors="input.colors"
                    :bundles="bundles"
                    :hat-bundles="hatBundles"
                    :beanie-bundles="beanieBundles"
                    :tote-bundles="toteBundles"
                    :stickers="stickers"
                    :is-choosing-color="isChoosingColor"
                    :color-limit="colorLimit"
                    :custom-size="customSize"
                    :colors-modal-id="colorsModalId"
                    :blank-modal-id="blankModalId"
                    :can-add-bundle="canAddBundle"
                    :is-admin="showExtraControls"
                    @choose-color="onChooseColor"
                    @choose-default="onChooseDefaultItem"
                    @select-color-preview="onSelectColorPreview"
                    @select-group="onSelectGroup"
                    @add-color="onAddColor"
                    @add-another-color="onAddAnotherColor"
                    @edit-color="onEditColor"
                    @delete-color="onDeleteColor"
                    @cancel-color="onCancelColor"
                    @make-primary-color="onMakePrimaryColor"
                    @cancel-choose-color="onCancelChooseColor"
                    @print-width-input="onPrintWidthInput"
                    @print-height-input="onPrintHeightInput"
                    @show-modal="showModal"
                  />
                </main>
              </NylonCollapse>
            </div>
            <footer class="options-footer" v-if="step === steps.BUILDER">
              <nav class="options-footer-actions">
                <button class="next" :disabled="!canLeaveBuilder" @click.prevent="onCompleteBuilder">Next</button>
              </nav>
            </footer>
          </main>
          <main class="nylon-SubmissionWizard-options" v-else-if="step === steps.DETAILS">
            <div class="options-container">
              <header class="options-header">
                <h2>Product Details</h2>
                <button class="show-preview" @click="showMobilePreview = !showMobilePreview">Preview</button>
                <button
                  type="button"
                  class="options-header-toggle"
                  :class="{ 'is-collapsed': collapsedBoxes.details }"
                  :aria-expanded="(!collapsedBoxes.details).toString()"
                  aria-label="Toggle section"
                  @click.prevent="onToggleBox('details')"
                ></button>
              </header>
              <NylonCollapse :collapsed="collapsedBoxes.details">
                <main class="options-content">
                  <NylonOptionPanel collapsible :body-class="{ error: v$.input.meta.title.$invalid }">
                    <template #header>
                      <h4>Title</h4>
                      <a href="#" class="help-link" @click.prevent="showModal(titleModalId)">?</a>
                    </template>
                    <p>This will be the title of your product so make it good—unique, catchy, short ones work best.</p>
                    <input type="text" v-model="v$.input.meta.title.$model" maxlength="85" required />
                    <div v-for="error of v$.input.meta.title.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>
                  </NylonOptionPanel>
                  <NylonOptionPanel collapsible :body-class="{ error: v$.input.meta.description.$invalid }">
                    <template #header>
                      <h4>
                        Description
                        <span>(Optional)</span>
                      </h4>
                      <a href="#" class="help-link" @click.prevent="showModal(descriptionModalId)">?</a>
                    </template>
                    <textarea v-model="v$.input.meta.description.$model" maxlength="2000"></textarea>
                    <div v-for="error of v$.input.meta.description.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>
                  </NylonOptionPanel>
                  <NylonPricingTable
                    collapsible
                    :items-grouped-for-pricing="itemsGroupedForPricing"
                    :synchronize-prices="synchronizePrices"
                    :is-pro-bono="c_isProBono"
                    :user="user"
                    :bonus-reduction-max="lifetimeBonusFromLifetimeTier()"
                    @profit-group-input="onProfitGroupInput"
                    @synchronize-prices-change="onSynchronizePricesChange"
                    @show-help="showModal(pricingModalId)"
                    @update:is-pro-bono="onProBonoToggle"
                  />
                  <NylonOptionPanel collapsible v-if="input.salesMethod === salesMethods.CAMPAIGN">
                    <template #header>
                      <h4>Campaign Settings</h4>
                      <a href="#" class="help-link" @click.prevent="showModal(campaignSettingsModalId)">?</a>
                    </template>
                    <div class="option-panel-balanced-cols">
                      <NylonFormField
                        class="option-panel-col"
                        type="datetime-local"
                        label="Start Date"
                        :model-value="v$.input.campaignStart.$model"
                        :min="campaignStartMin"
                        :is-error="v$.input.campaignStart.$invalid"
                        @update:model-value="(v) => (v$.input.campaignStart.$model = v)"
                      >
                        <template #note>Campaign will launch at the start time, or immediately upon approval if that time has passed.</template>
                        <template v-if="v$.input.campaignStart.$errors.length" #message>
                          <div v-for="error of v$.input.campaignStart.$errors" :key="error.$uid">{{ error.$message }}</div>
                        </template>
                      </NylonFormField>
                      <NylonFormField
                        class="option-panel-col"
                        type="datetime-local"
                        label="End Date"
                        :model-value="v$.input.campaignEnd.$model"
                        :min="campaignEndMin"
                        :max="campaignEndMax"
                        :is-error="v$.input.campaignEnd.$invalid"
                        @update:model-value="(v) => (v$.input.campaignEnd.$model = v)"
                      >
                        <template v-if="v$.input.campaignEnd.$errors.length" #message>
                          <div v-for="error of v$.input.campaignEnd.$errors" :key="error.$uid">{{ error.$message }}</div>
                        </template>
                      </NylonFormField>
                    </div>
                  </NylonOptionPanel>
                  <NylonOptionPanel collapsible v-if="input.salesMethod === salesMethods.CAMPAIGN" :body-class="{ error: v$.input.meta.notes.$invalid }">
                    <template #header>
                      <h4>
                        Notes
                        <span>(Optional)</span>
                      </h4>
                      <a href="#" class="help-link" @click.prevent="showModal(notesModalId)">?</a>
                    </template>
                    <p>Just between us, is there anything else we should know about this campaign?</p>
                    <textarea v-model="v$.input.meta.notes.$model" maxlength="2000"></textarea>
                    <div v-for="error of v$.input.meta.notes.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>
                  </NylonOptionPanel>
                  <NylonOptionPanel collapsible start-collapsed v-if="willLaunchAfterSubmission || isEodSubmission">
                    <template #header>
                      <h4>Advanced Settings</h4>
                    </template>
                    <div class="advanced">
                      <dl>
                        <div class="setting">
                          <dt>
                            <template v-if="willLaunchAfterSubmission">
                              <h5>Make this product available immediately after submitting?</h5>
                              <p class="explanation">Default is “yes”. Choose “no” if you want publish this product at a future date.</p>
                            </template>
                            <template v-else>
                              <h5>Publish this product as soon as it’s approved?</h5>
                              <p class="explanation">Default is “yes”. Choose “no” if you’d rather publish it yourself from your account after we approve it.</p>
                            </template>
                          </dt>
                          <dd>
                            <div class="setting-toggle-wrapper">
                              <combo-button
                                :is-small="true"
                                :combos="[
                                  {
                                    id: 'yes',
                                    text: 'Yes',
                                  },
                                  {
                                    id: 'no',
                                    text: 'No',
                                  },
                                ]"
                                :selected-combo="input.shouldPublish"
                                @combo-selected="onPublishSelected"
                              />
                            </div>
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </NylonOptionPanel>
                </main>
              </NylonCollapse>
            </div>
            <footer class="options-footer">
              <nav class="options-footer-actions">
                <div class="account-links" v-if="!user">
                  <div class="account-buttons">
                    <button class="account-button" @click.prevent="showModal(signUpModalId)">Create an Account</button>
                    <button class="account-button" @click.prevent="showModal(signInModalId)">Sign In</button>
                  </div>
                  <div>
                    <a href="#" class="why-link" @click.prevent="showModal(accountWhyModalId)">Why do I need an account?</a>
                  </div>
                </div>

                <div class="disclaimer disclaimer--caution active" v-if="input.productionMethod === productionMethods.EMBROIDERY">
                  <figure>
                    <span>!</span>
                  </figure>
                  <div class="figcaption">
                    <p><strong>Before you hit this button, make sure everything looks right (especially the artwork size).</strong></p>
                    <p>
                      Embroidery artwork goes through a process called “digitization” to be ready for production which can be both time-consuming and expensive. You’re doing us a big favor by
                      double-checking everything now. Thank you!
                    </p>
                  </div>
                </div>

                <button class="next" :disabled="!user || isSubmitting" @click.prevent="onSubmit">
                  <span :class="{ invisible: isSubmitting }">Finish & Submit</span>
                  <VueSpinner v-if="isSubmitting" color="#ff523f" />
                </button>

                <div v-for="error of v$.input.meta.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>

                <template v-if="input.salesMethod === salesMethods.CAMPAIGN">
                  <div v-for="error of v$.input.campaignStart.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>
                  <div v-for="error of v$.input.campaignEnd.$errors" :key="error.$uid" class="message">{{ error.$message }}</div>
                </template>

                <message-block v-if="submitMessage" type="error" :isDismissable="false">
                  <p>{{ submitMessage }}</p>
                </message-block>
              </nav>
            </footer>
          </main>
        </div>
      </section>

      <!-- /// confirmation step -->
      <section class="nylon-SubmissionWizard-content" v-if="step === steps.COMPLETE">
        <div class="wrapper">
          <div class="text">
            <p v-if="willLaunchAfterSubmission && willPublish">Got it! Your product is live.</p>
            <p v-else-if="requiresApproval && isEodSubmission && !willPublish">
              Got it! Please give us some time to review your submission. Once approved, it will wait in your account for you to publish.
            </p>
            <p v-else-if="requiresApproval">Got it! Please give us some time to review your submission.</p>
            <p v-else-if="accountIncomplete" class="alert">Got it! Please add all required account info.</p>
            <p v-else-if="willLaunchAfterSubmission && !willPublish">Got it! Your product is in your account for you to review.</p>
          </div>

          <template v-if="willLaunchAfterSubmission">
            <div class="action">
              <div class="product-links">
                <a class="see-product" :href="product.links.url" target="_blank">See Your Product</a>
                <a class="submit-another" href="/create-a-product" target="_blank">Create a Product</a>
              </div>
            </div>
          </template>

          <div v-if="willLaunchAfterSubmission" class="promote">
            <header>
              <h4>Promote Your Product</h4>
            </header>
            <main>
              <p>
                People can’t buy something if they don’t know it exists. And we don’t want that. With such a great design, you’ll be doing everyone a favor by cluing them in. Seriously, the difference
                between items that sell and items that don’t is the amount of promotion by the seller. Don’t worry, you got this.
              </p>
              <figure class="url">
                <label>Send everyone you can to this address:</label>
                <div class="url-wrapper">
                  <input type="text" :value="product.links.url" readonly />
                  <button class="copy" @click="onCopyProductUrl(product)">Copy URL</button>
                </div>
              </figure>
              <div class="promote-links">
                <figure class="mockups">
                  <label>Need visuals?</label>
                  <a class="mockup-download" @click.prevent="onDownloadMockups">
                    <i></i>
                    <h5>
                      <template v-if="!isSubmissionProcessed">
                        Get Mockup Images
                        <span>We’ll email you when they’re ready.</span>
                      </template>
                      <template v-else>
                        Request Received
                        <span>Stand back, servers are churning.</span>
                      </template>
                    </h5>
                  </a>
                </figure>
                <figure class="social">
                  <label>Make an announcement to your followers:</label>
                  <div class="social-wrapper">
                    <social-buttons :product="product" icon-class="icon" />
                  </div>
                </figure>
              </div>
            </main>
          </div>
          <a v-else class="mockup-download-strip" @click.prevent="onDownloadMockups">
            <i></i>
            <h5>
              <template v-if="!isSubmissionProcessed">
                Get Mockup Images
                <span>We’ll email you when they’re ready.</span>
              </template>
              <template v-else>
                Request Received
                <span>Stand back, servers are churning.</span>
              </template>
            </h5>
          </a>
          <div class="helpful-links">
            <header>
              <h4>Helpful Links</h4>
            </header>
            <main>
              <div class="helpful-links-wrapper">
                <ul>
                  <li v-if="willLaunchAfterSubmission && willPublish"><InertiaLink :href="product.links.url">This Product’s Page</InertiaLink></li>
                  <li><InertiaLink href="/account">Your Account</InertiaLink></li>
                  <li><InertiaLink :href="user.url">Your Public Profile</InertiaLink></li>
                  <li><a href="mailto:us@cottonbureau.com">Contact Us</a></li>
                </ul>
                <ul>
                  <li><InertiaLink href="/how-it-works">How it Works</InertiaLink></li>
                  <li><InertiaLink href="/terms-of-service">Terms of Service</InertiaLink></li>
                  <li><InertiaLink href="/privacy-policy">Privacy Policy</InertiaLink></li>
                </ul>
              </div>
            </main>
          </div>
        </div>
      </section>
    </template>

    <PolyModal :modal-id="blankModalId" v-if="modalData" :title="`${modalData.blank.brand} ${modalData.blank.code} ${modalData.color.name}`" @close="() => vfm$.close(blankModalId)">
      <img :src="blankMockupUrl(modalData)" class="v--modal-hero" />
      <p>Available in size {{ modalData.sizeRanges[input.productionMethod] }}.</p>
      <p>
        Visit
        <a :href="modalData.blank.brand_url" target="_blank">{{ modalData.blank.brand }} website</a>
        for more information.
      </p>
    </PolyModal>

    <PolyModal :modal-id="productTypeModalId" title="Product Type" @close="() => vfm$.close(productTypeModalId)">
      <p>
        Are you looking to sell something not listed here?
        <a href="mailto:us@cottonbureau.com">Email us</a>, we might be able to help.
      </p>
    </PolyModal>

    <PolyModal :modal-id="salesMethodModalId" title="Sales Method" @close="() => vfm$.close(salesMethodModalId)">
      <p>There are three ways to sell on Cotton Bureau: on demand, pre-order campaigns, and stock.</p>
      <p>
        The first two are self-serve (and, hopefully, self-explantory), but if you want to offer a stock product, <a href="mailto:us@cottonbureau.com">email us</a>, we might be able to help. There’s a
        whole universe of things that can only be made ahead of time.
      </p>
    </PolyModal>

    <PolyModal :modal-id="decorationMethodModalId" title="Decoration Method" @close="() => vfm$.close(decorationMethodModalId)">
      <p>Whether you’re looking for the highest quality print possible or something with a little more body, you’ve got options.</p>
      <p><a href="mailto:us@cottonbureau.com">Email us</a> if you don’t see what you’re looking for.</p>
    </PolyModal>

    <PolyModal :modal-id="sourceFileModalId" title="Source File" @close="() => vfm$.close(sourceFileModalId)">
      <p>
        If there are any issues with your artwork, we may be able to fix them if we have access to your original source file (AI, EPS, PDF, PSD, or SVG). This could save time and ensure a successful
        print, especially if you’re new to designing shirts. Uploading your source file is completely optional; it’s just a lot easier for us to ask for it now instead of hunting it down later.
      </p>
    </PolyModal>

    <PolyModal :modal-id="placementModalId" title="Placement" @close="() => vfm$.close(placementModalId)">
      <p v-if="input.productType === productTypes.APPAREL">Choose the size and position of your design and we’ll ensure it’s printed in the right spot.</p>
      <p v-else-if="input.productType === productTypes.PHONECASE">
        Choose the size and position of your design and we’ll ensure it’s printed in the right spot. Please keep in mind that max size exceeds the boundary of the phones and will be cropped as
        necessary.
      </p>
      <p v-else-if="input.productType === productTypes.HAT">
        <strong>Placement Dimensions</strong>
        <br />
        <br />
        Max: 5.5" wide by 2.25" tall
        <br />
        Large: 5.5" wide by 2" tall
        <br />
        Medium: 5.5" wide by 1.5" tall
        <br />
        Small: 5.5" wide by 1" tall
        <br />
        Extra Small: 5.5" wide by 0.5" tall
        <br />
        Custom: 0.25" – 5.5" wide by 0.25" – 2.25" tall
      </p>
      <p v-else-if="input.productType === productTypes.BEANIE">
        <strong>Placement Dimensions</strong>
        <br />
        <br />
        Max: 3.75" wide by 2.25" tall
        <br />
        Large: 3.75" wide by 2" tall
        <br />
        Medium: 3.75" wide by 1.5" tall
        <br />
        Small: 3.75" wide by 1" tall
        <br />
        Extra Small: 3.75" wide by 0.5" tall
        <br />
        Custom: 0.25" – 3.75" wide by 0.25" – 2.25" tall
      </p>
      <p v-else-if="input.productType === productTypes.TOTE">Choose the size and position of your design and we’ll ensure it’s printed in the right spot.</p>
      <p v-else-if="input.productType === productTypes.DRINKWARE">
        <strong>Need an exact match for a specific drinkware product?</strong>
        <br />
        Choose “Max” placement and use these file sizes.
        <br />
        <br />
        Everyday Tumbler: 6116&ThinSpace;ⅹ&ThinSpace;3430
        <br />
        Pint Glass: 5476&ThinSpace;ⅹ&ThinSpace;2550
        <br />
        Skinny Water Bottle: 5004&ThinSpace;ⅹ&ThinSpace;4606
        <br />
        Everyday Straw Cup: 5934&ThinSpace;ⅹ&ThinSpace;2552
        <br />
        Rocks Glass: 5960&ThinSpace;ⅹ&ThinSpace;1394
        <br />
        Skinny Can Sleeve: 5382&ThinSpace;ⅹ&ThinSpace;3142
        <br />
        Everyday Can Sleeve: 5684&ThinSpace;ⅹ&ThinSpace;2240
        <br />
        Everyday Mug: 5196&ThinSpace;ⅹ&ThinSpace;2268
      </p>
    </PolyModal>

    <PolyModal :modal-id="colorsModalId" title="Colors" @close="() => vfm$.close(colorsModalId)">
      <div v-if="input.productType === productTypes.APPAREL">
        <p>
          Each shirt color option represents a bundle of pre-selected garments so you don’t have to worry about choosing each t-shirt and sweatshirt individually. The primary color will be displayed
          as a representation of your product on the site by default (the first image shown on the product page and the shop index).
        </p>
        <p>
          Be sure to choose colors that complement your design and, you know, a little variety never hurt anyone. Black, grey, and navy are easily our most popular colors, so it’s always a good idea
          to include at least one of them (if it works with your design). You can choose up to {{ colorLimit }} separate color bundles per product, but don’t feel like you have to. 1–3 color choices
          is usually enough.
        </p>
      </div>
      <div v-else-if="input.productType === productTypes.PHONECASE">
        <p>Please choose a background color for your phone case from the pre-selected options or a custom color of your choosing!</p>
      </div>
      <div v-else-if="input.productType === productTypes.HAT">
        <p>
          Each color option represents a bundle of pre-selected hats so you don’t have to worry about choosing each style individually. The primary color will be displayed as a representation of your
          product on the site by default (the first image shown on the product page and the shop index). You can always remove any styles you don’t like by using the Edit button.
        </p>
        <p>
          Be sure to choose colors that complement your design and, you know, a little variety never hurt anyone. Black, grey, and navy are easily our most popular colors, so it’s always a good idea
          to include at least one of them (if it works with your design). You can choose up to {{ colorLimit }} separate color bundles per product, but don’t feel like you have to. 1–3 color choices
          is usually enough.
        </p>
      </div>
      <div v-else-if="input.productType === productTypes.BEANIE">
        <p>
          Each color option represents a bundle of pre-selected beanies so you don’t have to worry about choosing each style individually. The primary color will be displayed as a representation of
          your product on the site by default (the first image shown on the product page and the shop index). You can always remove any styles you don’t like by using the Edit button.
        </p>
        <p>
          Be sure to choose colors that complement your design and, you know, a little variety never hurt anyone. Black, grey, and navy are easily our most popular colors, so it’s always a good idea
          to include at least one of them (if it works with your design). You can choose up to {{ colorLimit }} separate color bundles per product, but don’t feel like you have to. 1–3 color choices
          is usually enough.
        </p>
      </div>
      <div v-else-if="input.productType === productTypes.TOTE">
        <p>
          Just a heads up, for natural-colored totes, any white ink in your design will become transparent. Light colors may also be slightly darker than expected. Last thing, cyan and magenta lose
          some vibrancy when printed on totes. If your design depends heavily on those colors, it may be more muted in person than you see here.
        </p>
      </div>
      <div v-else-if="input.productType === productTypes.DRINKWARE">
        <p>Please choose a background color for your drinkware from the pre-selected options or a custom color of your choosing!</p>
      </div>
    </PolyModal>

    <PolyModal :modal-id="campaignSettingsModalId" title="Campaign Settings" @close="() => vfm$.close(campaignSettingsModalId)">
      <p>Campaigns are typically approved within 1–2 business days.</p>
    </PolyModal>

    <PolyModal :modal-id="inkNumOptionsModalId" title="Ink Colors" @close="() => vfm$.close(inkNumOptionsModalId)">
      <p>FYI, each additional ink color after the first adds $1 to the base price. Think carefully about how many colors you need. We recommend 1–4 colors in most cases.</p>
    </PolyModal>

    <PolyModal :modal-id="inkTypeOptionsModalId" title="Ink Type" @close="() => vfm$.close(inkTypeOptionsModalId)">
      <img src="https://s3.amazonaws.com/cottonbureau.com/img/ink-samples/both.jpg" class="v--modal-hero v--modal-hero--detail u-push" />
      <p>Standard inks lay on top of the fabric, providing bright colors and a smooth feel.</p>
      <p>Water-based inks are absorbed by the fabric, blending in and providing a vintage look and transparent feel.</p>
    </PolyModal>

    <PolyModal :modal-id="titleModalId" title="Title" @close="() => vfm$.close(titleModalId)">
      <p>
        Everywhere your product is shown, the title will be as well. The title will also be part of your product’s URL, so something fairly unique might help. Keep it short and don’t overthink it!
      </p>
    </PolyModal>

    <PolyModal :modal-id="descriptionModalId" title="Description" @close="() => vfm$.close(descriptionModalId)">
      <p>
        A short passage of accompanying text can help give context, especially if the design is abstract or references a specific show/movie/game/event. Again, this doesn’t need to be a dissertation,
        but a few clever sentences can help make a sale.
      </p>
    </PolyModal>

    <PolyModal :modal-id="pricingModalId" title="Pricing" @close="() => vfm$.close(pricingModalId)">
      <p>
        Your profit margin is completely up to you, but choose wisely. If the retail price is too high it will be difficult to sell any products. Too low and you’ll need to sell a ton to see any
        meaningful amount of money. We recommend a $5 margin because it lands in the middle—a decent amount of profit and keeps the retail price reasonable (and in line with most products on the
        site).
      </p>
      <p>
        If you have earned a lifetime bonus, you can also spend it to lower your retail prices below the base price—all the way down to the base price minus your bonus. Lowering the price consumes
        that much of your per-item bonus; whatever is left over is still paid out on every sale as long as you have payment information on file.
      </p>
      <p v-if="input.productType === productTypes.APPAREL">
        The retail prices shown are for sizes up to 2X. Sizes 3X and up cost us more to make, so their base price—and retail price—is $3 higher for t-shirts, long sleeves, and tanks, and $5 higher for
        sweatshirts and hoodies. Your profit is the same on every size.
      </p>
      <p>
        Oh, and while we’re talking about price and profit: payouts are disbursed around the middle each month as long as we owe you $25 or more (if not, it rolls over month to month until we do).
        Also, you also receive a discounted price on your own products (just make sure you’re signed in and you’ll see your special price displayed directly on the product page).
      </p>
    </PolyModal>

    <PolyModal :modal-id="accountWhyModalId" title="Why an account?" @close="() => vfm$.close(accountWhyModalId)">
      <p>
        We need each product to be assigned to a person and are unable to do that without your information. As soon as the design is submitted, you will be able to track it in your account dashboard.
        We also need to be able to communicate with you regarding your product.
      </p>
    </PolyModal>

    <sign-in-modal :modal-id="signInModalId" @sign-in="onSignIn" @close="() => vfm$.close(signInModalId)"></sign-in-modal>

    <sign-up-modal :modal-id="signUpModalId" @sign-up="onSignUp" @close="() => vfm$.close(signUpModalId)"></sign-up-modal>
  </section>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { Link as InertiaLink } from "@inertiajs/vue3";
import StorefrontLayout from "@/layout/storefront-layout.vue";
const ComboButton = defineAsyncComponent(() => import("@/components/combo-button.vue"));
const NylonColorStylePanel = defineAsyncComponent(() => import("@/nylon/components/NylonColorStylePanel.vue"));
import ThreeDimensionalModel from "@/components/three-dimensional-model-async.js";
const NylonProductPreviewPanel = defineAsyncComponent(() => import("@/nylon/components/NylonProductPreviewPanel.vue"));
const NylonPricingTable = defineAsyncComponent(() => import("@/nylon/components/NylonPricingTable.vue"));
const SignInModal = defineAsyncComponent(() => import("@/authentication/sign-in-modal.vue"));
const SignUpModal = defineAsyncComponent(() => import("@/authentication/sign-up-modal.vue"));
const SocialButtons = defineAsyncComponent(() => import("@/components/social-buttons.vue"));

import NylonOptionPanel from "@/nylon/components/NylonOptionPanel.vue";
import NylonAddButton from "@/nylon/components/NylonAddButton.vue";
import NylonCollapse from "@/nylon/components/NylonCollapse.vue";
import NylonOptionGrid from "@/nylon/components/NylonOptionGrid.vue";
import NylonSegmentedControl from "@/nylon/components/NylonSegmentedControl.vue";
import NylonFormField from "@/nylon/components/NylonFormField.vue";
import NylonPlacementOptions from "@/nylon/components/NylonPlacementOptions.vue";
import NylonStepMarker from "@/nylon/components/NylonStepMarker.vue";
import PolyModal from "@/nylon/components/PolyModal.vue";
import ScreenPrintInkPanels from "@/components/screen-print-ink-panels.vue";
import { DateTime } from "luxon";
import { helpers } from "@vuelidate/validators";
import required from "@/validators/required.js";
import maxLength from "@/validators/max-length.js";
import { mapState, mapStores } from "pinia";
import { useVfm } from "vue-final-modal";
import { useUserStore } from "@/store/user.js";
import { useAssistantStore } from "@/store/assistant.js";
import { creatorSnapshot } from "@/lib/creator-assistant-snapshot.js";
import { applyCreatorChanges } from "@/lib/creator-assistant-changes.js";
import { useSubmissionStore } from "@/submission/store/submission.js";
import { useSubmissionBundlesStore } from "@/submission/store/bundles.js";
import { useAccountProductStore } from "@/account/store/product.js";
import { useVuelidate } from "@vuelidate/core";
import clone from "clone";
import ProductCreatorMixin from "@/mixins/product-creator.js";

import {
  modalIds,
  createDefaultInput,
  createDefaultData,
  PLACEHOLDER_ARTWORK_PATH_INFO,
  PLACEHOLDER_DARK_ARTWORK_PATH_INFO,
  DEFAULT_PREVIEW_BLANK,
  DEFAULT_PREVIEW_DRINKWARE,
  customPlacements,
} from "@/lib/product-creator.js";

// Single source of truth for the wizard's step order. Each entry maps a step (the values
// from the mixin's `steps`) to its URL hash (Setup + Artwork share the base, no hash), its
// progress-marker number (Artwork + Builder share marker 2 — the artwork dropzone is step 2's
// empty entry state), and whether it's the primary step a marker click targets (Builder, not
// the empty Artwork start; never the terminal Complete screen). hashForStep / stepIndex /
// marker click targets are all derived from this.
const STEP_FLOW = [
  { step: "setup", hash: "", marker: 1, markerTarget: true },
  { step: "artwork", hash: "", marker: 2, markerTarget: false },
  { step: "builder", hash: "#builder", marker: 2, markerTarget: true },
  { step: "details", hash: "#details", marker: 3, markerTarget: true },
  { step: "complete", hash: "#complete", marker: 4, markerTarget: false },
];
const STEP_FLOW_BY_STEP = Object.fromEntries(STEP_FLOW.map((entry) => [entry.step, entry]));

// Custom or normal: the account's seller type once set, otherwise the tool's flow.
function isCustomSellerFor(user, isCustomFlow) {
  return user?.sellerType ? user.sellerType === "custom" : isCustomFlow;
}

// The pro bono default: an account's payment type decides once it has one; a seller
// account without one is always for-profit (the server won't record pro bono for it);
// otherwise custom sellers default to pro bono and normal sellers to for-profit.
function defaultIsProBono(user, isCustomFlow) {
  if (user?.paymentType) {
    return user.paymentType === "probono";
  }

  // A seller account with no payment type can't become pro bono on submit
  // (UserResource::canChooseProBono), so it prices as for-profit.
  if (user && !user.canChooseProBono) {
    return false;
  }

  return isCustomSellerFor(user, isCustomFlow);
}

export default {
  name: "CreateAProduct",
  layout: StorefrontLayout,

  setup() {
    const vfm = useVfm();
    const signInModalId = Symbol("signInModalId");
    const signUpModalId = Symbol("signUpModalId");
    const productTypeModalId = Symbol("productTypeModalId");
    const salesMethodModalId = Symbol("salesMethodModalId");
    const decorationMethodModalId = Symbol("decorationMethodModalId");

    return {
      v$: useVuelidate(),
      signInModalId,
      signUpModalId,
      productTypeModalId,
      salesMethodModalId,
      decorationMethodModalId,
      ...modalIds,
      vfm$: vfm,
    };
  },

  components: {
    NylonAddButton,
    NylonCollapse,
    NylonColorStylePanel,
    ComboButton,
    InertiaLink,
    NylonFormField,
    NylonOptionGrid,
    NylonSegmentedControl,
    NylonOptionPanel,
    NylonPlacementOptions,
    NylonStepMarker,
    PolyModal,
    NylonPricingTable,
    ScreenPrintInkPanels,
    NylonProductPreviewPanel,
    SignInModal,
    SignUpModal,
    SocialButtons,
    ThreeDimensionalModel,
  },

  mixins: [ProductCreatorMixin],

  props: {
    isCustomFlow: {
      type: Boolean,
      default: false,
    },
    createFromExistingArtwork: { type: Object, default: null },
    relaunchCampaign: { type: Object, default: null },
    initialUser: { type: Object, default: null },
  },

  computed: {
    ...mapStores(useUserStore, useSubmissionStore, useAccountProductStore, useSubmissionBundlesStore),

    // Artwork entry state: live mockup of the product type's default item
    // wearing the dashed print-area placeholder at the type's default
    // placement — rendered by the same pipeline (and on the same item) as the
    // post-upload preview, so the hand-off is seamless. Apparel pins the
    // shared default preview blank (CC 1717 Pepper); other types take the
    // server's default item, as the post-upload preview does before an item
    // is chosen. Stickers are white, so they get the dark placeholder.
    // Server-side render cache makes repeat visits instant.
    entryPlaceholderMockupUrl() {
      const isApparel = this.input.productType === this.productTypes.APPAREL;
      const isSticker = this.input.productType === this.productTypes.STICKER;
      const query = this.mockupQuery({
        pathInfo: isSticker ? PLACEHOLDER_DARK_ARTWORK_PATH_INFO : PLACEHOLDER_ARTWORK_PATH_INFO,
        w: this.mockupWidth,
        placement: isApparel ? "large+centered" : this.input.placement,
        ...(isApparel ? DEFAULT_PREVIEW_BLANK : {}),
      });

      return this.mockupUrl(query);
    },

    defaultDrinkware() {
      return DEFAULT_PREVIEW_DRINKWARE;
    },

    // Drinkware's entry state: the placeholder as the 3D model's texture.
    entryPlaceholderTextureUrl() {
      return this.textureUrl(this.mockupQuery({ pathInfo: PLACEHOLDER_ARTWORK_PATH_INFO, placement: this.input.placement, width: 2048 }));
    },

    // Gate for advancing past Setup → Artwork/Builder. Single source of truth shared by the
    // Setup "Next" button (:disabled) and the progress markers (canJumpTo), so a marker can
    // never reach a step the button wouldn't let you into.
    canLeaveSetup() {
      return !!this.input.productType && (this.input.productType !== this.productTypes.APPAREL || !!this.input.salesMethod);
    },

    // Gate for advancing past the Artwork dropzone → Builder (auto-advances on upload).
    canLeaveArtwork() {
      return !!this.input.artwork.rasterArtworkPathInfo && !this.isRasterFileUploading;
    },

    // Gate for advancing past Builder → Details (shared with the Builder "Next" button).
    // The screen-print source-file requirement lives here now that the Decoration Method
    // and Source File panels are part of the Builder step.
    canLeaveBuilder() {
      const needsSourceArtwork = [this.productionMethods.SCREENPRINT].includes(this.input.productionMethod);
      const hasSourceArtwork = !!this.input.artwork.sourceArtworkPathInfo;

      return this.input.colors.length > 0 && (!needsSourceArtwork || hasSourceArtwork) && !this.isRasterFileUploading && !this.isSourceFileUploading && this.canLeaveSecondLocation;
    },

    // A second location, once added, must be complete before leaving the builder —
    // or removed.
    canLeaveSecondLocation() {
      const second = this.input.secondLocation;

      if (!second) {
        return true;
      }

      const hasSourceArtwork = !this.secondIsScreenPrint || !!second.artwork.sourceArtworkPathInfo;

      return !!second.artwork.rasterArtworkPathInfo && hasSourceArtwork && !this.isSecondRasterFileUploading && !this.isSecondSourceFileUploading;
    },

    // (showSecondRaster / secondPlacements / secondCustomSize live in the
    // product-creator mixin, shared with account/edit-product.)

    willLaunchAfterSubmission() {
      // Mirrors SubmissionController: launching needs a complete account (payout method fully set
      // up). A seller signing up in this form is only ready if they're going pro bono.
      const isReady = this.user ? !!this.user.readiness?.isReadyForLaunch : this.c_isProBono;

      return isReady && !this.requiresApproval;
    },

    // Embroidery on-demand (hats, beanies) always requires approval; the auto-publish
    // preference is honored when the admin accepts the product rather than at submit time.
    isEodSubmission() {
      return this.input.salesMethod === this.salesMethods.ONDEMAND && this.input.productionMethod === this.productionMethods.EMBROIDERY;
    },

    willPublish() {
      return this.input.shouldPublish.id === "yes";
    },

    // An account the submit endpoint would refuse, or one at its product limit, gets a
    // message instead of the wizard.
    isBlocked() {
      return !!this.user && (!this.user.canSubmitDesigns || !this.user.canCreateProduct);
    },

    // The account's seller type once it has one; until then the tool decides, because the
    // first submission sets seller_type from the flow it came through
    // (SubmissionController::post_submit_designs).
    isCustomSeller() {
      return isCustomSellerFor(this.user, this.isCustomFlow);
    },

    requiresApproval() {
      const isOnDemand = this.input.salesMethod === this.salesMethods.ONDEMAND;
      const isNotEmbroidery = this.input.productionMethod !== this.productionMethods.EMBROIDERY;
      const hasPreviousAcceptedProducts = !!this.user?.hasPreviousAcceptedProducts;

      // Mirrors SubmissionController::post_submit_designs, which decides on the account's seller type.
      const doesNotRequireApproval = (hasPreviousAcceptedProducts || this.isCustomSeller) && isOnDemand && isNotEmbroidery;
      return !doesNotRequireApproval;
    },

    accountIncomplete() {
      return !this.user?.readiness?.isReadyForLaunch;
    },

    headerText() {
      if (this.step !== this.steps.COMPLETE) {
        return "Create a Product";
      }

      return this.willLaunchAfterSubmission ? "Product Created" : "Product Submitted";
    },
  },

  data() {
    return {
      ...createDefaultData(),

      // Entry-state preview: the live dashed-placeholder mockup has loaded
      // (a spinner shows until then).
      entryPlaceholderLoaded: false,
      entryPlaceholderFailed: false,

      bundles: [],
      phoneCases: [],
      drinkware: [],
      stickers: { bySize: [] },
      hatBundles: [],
      beanieBundles: [],
      toteBundles: [],
      step: this.steps.SETUP,
      // Highest marker index the user has reached. Steps up to here have been visited (so
      // their prerequisites are met) and stay clickable even when they're ahead of the
      // current step. Persisted so it survives a Back-induced remount.
      furthestStepIndex: 1,
      topLeftHex: null,

      sourceFile: null,
      isSourceFileUploading: false,

      // (collapsedBoxes and the second-location upload machinery —
      // secondRasterFile/secondNoTrim/etc. — come from createDefaultData(),
      // shared with account/edit-product.)

      input: {
        ...createDefaultInput(),
        artwork: {
          rasterArtworkPathInfo: null,
          sourceArtworkPathInfo: null,
        },
        meta: {
          title: "",
          description: "",
          notes: "",
        },
        shouldPublish: { id: "yes" },
        isCustomFlow: this.isCustomFlow,
      },

      user: this.initialUser,
      product: null,

      isProBono: defaultIsProBono(this.initialUser, this.isCustomFlow),
      // Set once the visitor ticks or unticks pro bono themselves; signing in then keeps their choice.
      proBonoTouched: false,

      // Mockup archive generation
      isSubmissionProcessed: false,

      showExtraControls: false,
    };
  },

  watch: {
    // Switching product type (Setup → back → Artwork) swaps the entry
    // mockup: show the spinner until the new render lands.
    entryPlaceholderMockupUrl() {
      this.entryPlaceholderLoaded = false;
      this.entryPlaceholderFailed = false;
    },
  },

  methods: {
    init() {
      Object.assign(this.input, {
        placement: "large+centered",
        printGeometry: {
          width: 9,
          height: 14,
          unit: "in",
        },
        printOffset: {
          x: 0,
          y: 0,
          unit: "in",
        },
        mockupOptions: {
          noScale: false,
        },
        colors: [],
        campaignStart: this.campaignStartMin,
        campaignEnd: DateTime.fromISO(this.campaignStartMin).plus({ weeks: 2 }).toISO({
          format: "extended",
          suppressSeconds: true,
          suppressMilliseconds: true,
          includeOffset: false,
          extendedZone: false,
        }),
      });

      Object.assign(this.debouncedInput, {
        printGeometry: {
          width: 9,
          height: 14,
          unit: "in",
        },
        printOffset: {
          x: 0,
          y: 0,
          unit: "in",
        },
      });

      this.isChoosingColor = false;
    },

    async onChangeStep(step) {
      const changed = step !== this.step;

      this.step = step;

      if ([this.productTypes.DRINKWARE].includes(this.input.productType) && step === this.steps.BUILDER) {
        if (this.input.colors.length === 0) {
          this.onChooseColor(undefined, undefined, "#00000000");
          this.onEditColor(this.input.colors[0]);
        }
      }

      this.furthestStepIndex = Math.max(this.furthestStepIndex, this.stepIndex(step));
      this.goToStepUrl(step);

      // Every real step move lands the viewport at the header's inner
      // .wrapper (the title + step markers row — the header element itself
      // is pulled up behind the site nav by its negative top), offset by the
      // fixed site header's live height (44px mobile / 52px desktop) so the
      // row isn't occluded. Guarded on an actual change so the mounted()
      // initializers (which re-assert the current step to normalize the URL)
      // don't scroll on page load.
      if (changed) {
        await this.$nextTick();

        const siteHeader = document.querySelector("header.nylon-Header");
        this.$scrollTo("#nylon-SubmissionWizard-header > .wrapper", {
          offset: siteHeader ? -siteHeader.getBoundingClientRect().height : 0,
        });
      }
    },

    // --- Per-step URLs (hash) --------------------------------------------------------
    // Setup and Artwork share the base URL (no hash). In-app step changes only update the URL
    // hash via the History API, so the component stays mounted and wizard state is kept.
    hashForStep(step) {
      return STEP_FLOW_BY_STEP[step]?.hash ?? "";
    },

    // 1 = setup, 2 = artwork/builder, 3 = details, 4 = complete. Used for marker ordering.
    stepIndex(step) {
      return STEP_FLOW_BY_STEP[step]?.marker ?? 1;
    },

    goToStepUrl(step) {
      // Drive the URL via the History API (hash only), not Inertia: a hash change keeps the
      // component mounted (no server round-trip, no remount), so wizard state is preserved on
      // in-app nav. A browser Back/forward makes Inertia remount the page fresh; state is not
      // persisted, so mounted() normalizes the URL back to step 1 (losing in-progress work).
      const target = window.location.pathname + window.location.search + this.hashForStep(step);

      // Setup <-> Artwork (both the base URL, no hash) must not push a redundant entry.
      if (window.location.pathname + window.location.search + window.location.hash === target) {
        return;
      }

      try {
        // Complete replaces the entry so Back from the confirmation screen leaves the wizard
        // rather than re-entering the just-submitted Details form. Preserve window.history.state
        // so Inertia's own page state isn't clobbered (per store/product-selector.js).
        if (step === this.steps.COMPLETE) {
          window.history.replaceState(window.history.state, "", target);
        } else {
          window.history.pushState(window.history.state, "", target);
        }
      } catch {
        /* history throttling — no-op */
      }
    },

    // --- Clickable step markers ------------------------------------------------------
    // Any already-visited step is clickable except the one you're on — so a step you've
    // filled in is reachable again even when it's ahead of the current step. Not on the
    // terminal Complete screen, and never a step whose entry gate isn't currently satisfied.
    canJumpTo(markerIndex) {
      return this.step !== this.steps.COMPLETE && markerIndex !== this.stepIndex(this.step) && markerIndex <= this.furthestStepIndex && this.stepGateMet(markerIndex);
    },

    // A marker is only reachable when the state satisfies every gate up to it — the same
    // conditions the "Next" buttons enforce. So invalidating an earlier step (e.g. removing the
    // artwork after moving on) disables the markers for the steps it unlocked, matching the
    // now-disabled Next button.
    stepGateMet(markerIndex) {
      if (markerIndex >= 2 && !this.canLeaveSetup) {
        return false;
      }
      if (markerIndex >= 3 && (!this.canLeaveArtwork || !this.canLeaveBuilder)) {
        return false;
      }
      return true;
    },

    onMarkerClick(markerIndex) {
      if (!this.canJumpTo(markerIndex)) {
        return;
      }

      // The step this marker targets (marker 2 → Builder, since artwork is already uploaded once
      // past the dropzone, matching the in-page "Back" link from Details).
      const target = STEP_FLOW.find((entry) => entry.marker === markerIndex && entry.markerTarget);
      if (target) {
        this.onChangeStep(target.step);
      }
    },

    // A full page load or a browser Back/forward remount lands here with a stale hash (e.g.
    // /create-a-product#builder) but no in-memory wizard state, so start at step 1 and strip
    // the hash.
    normalizeStaleDeepUrl() {
      if (window.location.hash) {
        try {
          window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
        } catch {
          /* history throttling — no-op */
        }
      }
    },

    // Only apparel relaunches as a campaign. A hat or tote converts to on-demand, on the sales
    // and production methods its type defaults to (a legacy row's production method is often blank).
    seedRelaunchProductType() {
      this.onProductTypeChange(this.relaunchCampaign.productType);

      if (this.input.productType === this.productTypes.APPAREL) {
        this.onSalesMethodChange(this.salesMethods.CAMPAIGN);
        this.onProductionMethodChange(this.relaunchCampaign.productionMethod);
      }
    },

    // SETUP
    onCompleteSetup() {
      // Artwork already on file (relaunch, create-from-existing, or returning to Setup after
      // an upload) skips the dropzone and goes straight to the Builder.
      this.onChangeStep(this.input.artwork.rasterArtworkPathInfo ? this.steps.BUILDER : this.steps.ARTWORK);

      if (this.relaunchCampaign && this.input.colors.length === 0) {
        this.loadColorsItemsAndPricing(this.relaunchCampaign.activeBlankColors);

        if (this.input.colors.length > 0) {
          this.onSelectColorPreview(this.input.colors[0]);
        }
      }
    },

    // ARTWORK — overrides the mixin handler: the Artwork options-container now serves both
    // the artwork entry state and the builder, so a successful upload on the entry state
    // advances into the Builder (revealing Colors + the second-location button).
    dzRasterArtworkSuccess2(file, response) {
      ProductCreatorMixin.methods.dzRasterArtworkSuccess2.call(this, file, response);

      if (this.step === this.steps.ARTWORK) {
        this.onChangeStep(this.steps.BUILDER);
      }
    },

    onProductTypeChange(productType) {
      const changed = productType !== this.input.productType;

      this.input.productType = productType;

      if (changed) {
        this.init();
        this.onRemoveSecondLocation();

        this.onSalesMethodChange(this.salesMethods.ONDEMAND);
        this.onProductionMethodChange(this.productionMethods.DIRECTTOGARMENT);

        if (productType === this.productTypes.APPAREL) {
          this.input.placement = "large+centered";
        }

        if (productType === this.productTypes.PHONECASE) {
          this.input.placement = "medium+centered";
        }

        if (productType === this.productTypes.HAT || productType === this.productTypes.BEANIE) {
          this.input.placement = "medium+centered";
          this.onProductionMethodChange(this.productionMethods.EMBROIDERY);
        }

        if (productType === this.productTypes.TOTE) {
          this.input.placement = "medium+centered";
        }

        if (productType === this.productTypes.DRINKWARE) {
          this.input.placement = "medium+centered";
        }

        if (productType === this.productTypes.STICKER) {
          this.input.placement = "max+centered";
        }

        const width = this.customSize.default.width;
        this.debouncedInput.printGeometry.width = width;
        this.input.printGeometry.width = width;

        const height = this.customSize.default.height;
        this.debouncedInput.printGeometry.height = height;
        this.input.printGeometry.height = height;
      }
    },

    onSalesMethodChange(salesMethod) {
      const changed = salesMethod !== this.input.salesMethod;

      this.input.salesMethod = salesMethod;

      if (changed) {
        this.init();
        this.onRemoveSecondLocation();

        if (this.input.productType === this.productTypes.APPAREL) {
          if (this.input.salesMethod === this.salesMethods.CAMPAIGN) {
            if (![this.productionMethods.SCREENPRINT, this.productionMethods.EMBROIDERY].includes(this.input.productionMethod)) {
              this.onProductionMethodChange(this.productionMethods.SCREENPRINT);
            }
          } else if (this.input.salesMethod === this.salesMethods.ONDEMAND) {
            if (![this.productionMethods.DIRECTTOGARMENT, this.productionMethods.EMBROIDERY].includes(this.input.productionMethod)) {
              this.onProductionMethodChange(this.productionMethods.DIRECTTOGARMENT);
            }
          }

          if (this.input.productionMethod === this.productionMethods.EMBROIDERY) {
            this.input.placement = "pocket+medium";
          } else {
            this.input.placement = "large+centered";
          }
        }
      }
    },

    onProductionMethodChange(productionMethod) {
      const changed = productionMethod !== this.input.productionMethod;

      this.input.productionMethod = productionMethod;

      // Embroidery hides "Preserve artwork padding" (PRO-996), so a box ticked
      // under DTG must not carry the padded file over to it unseen.
      if (productionMethod === this.productionMethods.EMBROIDERY && this.noTrim) {
        this.toggleNoTrim();
      }

      if (changed) {
        if (this.input.productType === this.productTypes.APPAREL) {
          if (this.input.productionMethod === this.productionMethods.EMBROIDERY) {
            this.input.placement = "pocket+medium";
          } else {
            this.input.placement = "large+centered";
          }
        }

        const width = this.customSize.default.width;
        this.debouncedInput.printGeometry.width = width;
        this.input.printGeometry.width = width;

        const height = this.customSize.default.height;
        this.debouncedInput.printGeometry.height = height;
        this.input.printGeometry.height = height;

        // The back print rides on the primary being DTG — a method change
        // invalidates it (same treatment as product-type/sales-method changes).
        this.onRemoveSecondLocation();
      }
    },

    // BUILDER
    onCompleteBuilder() {
      this.onChangeStep(this.steps.DETAILS);
    },

    // (onToggleBox / scrollToBoxAfterCollapse and the whole second-print-
    // location suite — onAddSecondLocation / onRemoveSecondLocation /
    // toggleSecondNoTrim / dzSecondRasterArtwork* / onSecond* placement and
    // size handlers — live in the product-creator mixin, shared with
    // account/edit-product.)

    // DETAILS
    onPublishSelected(newSelection) {
      this.input.shouldPublish = newSelection;
    },

    async onSubmit() {
      this.v$.$touch();
      this.submitMessage = "";

      if (!this.v$.$invalid) {
        try {
          this.isSubmitting = true;
          this.syncDefaultItem();

          const response = await this.submissionStore.submit({
            ...this.input,
            campaignStart: DateTime.fromISO(this.input.campaignStart).toUTC(),
            campaignEnd: DateTime.fromISO(this.input.campaignEnd).toUTC(),
            colors: this.input.colors,
            isProBono: this.c_isProBono,
          });

          this.user = response.data.user;
          this.product = response.data.product;

          this.onChangeStep(this.steps.COMPLETE);
        } catch (error) {
          this.submitMessage = error.response?.data?.message || "An unknown error occurred.";
        } finally {
          this.isSubmitting = false;
        }
      }
    },

    // THANK YOU
    async onCopyProductUrl(product) {
      navigator.clipboard.writeText(product.links.url);
    },

    async onDownloadMockups() {
      if (this.isSubmissionProcessed) {
        return;
      }

      try {
        await this.accountProductStore.generateMockupArchive(this.product.id);
        this.isSubmissionProcessed = true;
      } catch (error) {
        console.error("Failed to request mockup archive:", error);
      }
    },

    // AUTHENTICATION MODALS
    async onSignIn(response) {
      this.applySignedInUser(response.data.user, response.data.userBaseAdjustment);
      this.vfm$.close(this.signInModalId);
    },

    async onSignUp(response) {
      // A new account never has a per-user base adjustment.
      this.applySignedInUser(response.data.user, 0);
      this.vfm$.close(this.signUpModalId);
    },

    onProBonoToggle(value) {
      this.proBonoTouched = true;
      this.c_isProBono = value;
    },

    // Signing in mid-flow makes the tool match the account, as if the page had loaded
    // signed in: its seller type (isCustomSeller) and, unless the visitor chose
    // themselves, its pro bono default. Prices derive from each item's chosen cut, so
    // the signed-in context (base adjustments, lifetime bonus) re-prices them by itself
    // and the visitor's choices are kept; only a change in pro bono status resets them.
    // The seller's own base adjustment comes with the sign-in response (pricingTables
    // was built for the visitor), so prices are right before this returns.
    applySignedInUser(user, userBaseAdjustment) {
      const wasProBono = this.c_isProBono;
      this.user = user;
      this.userBaseAdjustment = userBaseAdjustment ?? 0;

      // A choice the server would ignore (canChooseProBono false) is overridden too.
      if (!this.proBonoTouched || !user.canChooseProBono) {
        this.isProBono = defaultIsProBono(user, this.isCustomFlow);
      }

      if (this.c_isProBono !== wasProBono) {
        this.resetItemPricingToDefault();
      }
    },
  },

  async created() {
    this.showExtraControls = this.userStore.asAdmin;

    this.init();

    await this.submissionBundlesStore.load();
    this.bundles = clone(this.submissionBundlesStore.bundles);
    this.phoneCases = clone(this.submissionBundlesStore.phoneCases);
    this.drinkware = clone(this.submissionBundlesStore.drinkware);
    this.stickers = clone(this.submissionBundlesStore.stickers);
    this.hatBundles = clone(this.submissionBundlesStore.hatBundles);
    this.beanieBundles = clone(this.submissionBundlesStore.beanieBundles);
    this.toteBundles = clone(this.submissionBundlesStore.toteBundles);
    this.initializeBundles();

    this.sortPhoneCases();

    if (this.createFromExistingArtwork) {
      this.input.artwork.rasterArtworkPathInfo = this.createFromExistingArtwork.pathInfo;
      this.rasterGeometry = this.createFromExistingArtwork.geometry;
      this.topLeftHex = this.createFromExistingArtwork.topLeftHex;
      this.input.meta.createdFromId = this.createFromExistingArtwork.id;
      this.input.meta.replaceOldProduct = false;
      this.input.meta.title = this.createFromExistingArtwork.title;
      this.input.meta.description = this.createFromExistingArtwork.description;
      this.onProductTypeChange(this.createFromExistingArtwork.toType);
      this.onChangeStep(this.steps.SETUP);
    }

    if (this.relaunchCampaign) {
      if (this.relaunchCampaign.sourceArtworkPathInfo) {
        this.input.artwork.sourceArtworkPathInfo = this.relaunchCampaign.sourceArtworkPathInfo;
      }

      if (this.relaunchCampaign.rasterArtworkPathInfo) {
        this.topLeftHex = this.relaunchCampaign.topLeftHex;
        this.input.meta.createdFromId = this.relaunchCampaign.id;
        this.input.meta.replaceOldProduct = true;
        this.input.meta.title = this.relaunchCampaign.title;
        this.input.meta.description = this.relaunchCampaign.description;
        this.seedRelaunchProductType();

        this.input.placement = this.relaunchCampaign.placement;

        if (customPlacements.includes(this.input.placement)) {
          this.input.printGeometry = clone(this.relaunchCampaign.printGeometry);
          this.debouncedInput.printGeometry = clone(this.relaunchCampaign.printGeometry);
        }

        this.rasterGeometry = clone(this.relaunchCampaign.rasterGeometry);
        this.input.artwork.rasterArtworkPathInfo = this.relaunchCampaign.rasterArtworkPathInfo;
        this.input.printOffset = clone(this.relaunchCampaign.printOffset);
        this.debouncedInput.printOffset = clone(this.input.printOffset);

        if (this.relaunchCampaign.mockupOptions) {
          this.input.mockupOptions = clone(this.relaunchCampaign.mockupOptions);
        }

        if (this.input.productType === this.productTypes.APPAREL) {
          if (this.input.productionMethod === this.productionMethods.SCREENPRINT) {
            this.input.numInks = this.relaunchCampaign.numInks;
            this.input.inkType = this.relaunchCampaign.inkType;
            this.input.inkEffect = this.relaunchCampaign.inkEffect;
          }
        }

        // The back print relaunches with the campaign (PRO-3173).
        const second = this.relaunchCampaign.secondLocation;

        if (second) {
          this.seedSecondLocation();

          Object.assign(this.input.secondLocation, {
            placement: second.placement,
            numInks: second.numInks ?? 1,
            inkType: second.inkType ?? "plastisol",
            inkEffect: second.inkEffect ?? "none",
          });
          this.input.secondLocation.artwork.rasterArtworkPathInfo = second.rasterArtworkPathInfo;
          this.input.secondLocation.artwork.sourceArtworkPathInfo = second.sourceArtworkPathInfo;
          this.secondRasterGeometry = clone(second.rasterGeometry);

          if (customPlacements.includes(second.placement)) {
            this.input.secondLocation.printGeometry = clone(second.printGeometry);
            this.debouncedSecondInput.printGeometry = clone(second.printGeometry);
          }

          this.input.secondLocation.printOffset = clone(second.printOffset);
          this.debouncedSecondInput.printOffset = clone(second.printOffset);
        }

        this.onChangeStep(this.steps.SETUP);
      } else {
        this.input.meta.createdFromId = this.relaunchCampaign.id;
        this.input.meta.replaceOldProduct = true;
        this.input.meta.title = this.relaunchCampaign.title;
        this.input.meta.description = this.relaunchCampaign.description;
        this.seedRelaunchProductType();
      }
    }

    this.normalizeStaleDeepUrl();
  },

  // The account assistant sees what's being made here (PRO-3491): each chat message carries a
  // snapshot of the wizard. Nothing is sent until the seller asks something.
  mounted() {
    useAssistantStore().setPageContext({
      intro:
        "Ask about the product you’re making (which items and colours to offer, pricing and what you’ll earn, on-demand or campaign, your artwork) or ask me to change it: colours, prices, placement, the title and description.",
      snapshot: () => creatorSnapshot(this),
      // Phase 2: the assistant's changes, through the creator's own handlers. Nothing is saved
      // until the seller submits.
      applyChanges: (changes) => applyCreatorChanges(this, changes),
    });
  },

  beforeUnmount() {
    useAssistantStore().setPageContext(null);
  },

  validations() {
    const validations = {
      input: {
        meta: {
          title: {
            required: helpers.withMessage("Title is required.", required),
            maxLength: helpers.withMessage(({ $params }) => `Title must have at most ${$params.max} characters.`, maxLength(85)),
          },
          description: {
            maxLength: helpers.withMessage(({ $params }) => `Description must have at most ${$params.max} characters.`, maxLength(2000)),
          },
        },
      },
    };

    if (this.input.salesMethod === this.salesMethods.CAMPAIGN) {
      validations.input = {
        ...validations.input,
        campaignStart: {
          min: helpers.withMessage("Start date cannot be in the past.", (value) => {
            const startMin = DateTime.fromISO(this.campaignStartMin);
            const date = DateTime.fromISO(value);
            return date.isValid && date >= startMin;
          }),
        },
        campaignEnd: {
          min: helpers.withMessage("End date must be at least 24 hours after the start date.", (value) => {
            const endMin = DateTime.fromISO(this.campaignEndMin);
            const date = DateTime.fromISO(value);
            return date.isValid && date >= endMin;
          }),
          max: helpers.withMessage("End date must be at most 30 days after the start date.", (value) => {
            const endMax = DateTime.fromISO(this.campaignEndMax);
            const date = DateTime.fromISO(value);
            return date.isValid && date <= endMax;
          }),
        },
      };

      validations.input.meta.notes = {
        maxLength: helpers.withMessage(({ $params }) => `Notes must have at most ${$params.max} characters.`, maxLength(2000)),
      };
    }

    return validations;
  },
};
</script>
